import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/flights/fare-options';

const BAGGAGE_TYPES = ['Handbag', 'Cabin Baggage', 'Checked Baggage'];

/**
 * Map API fare option to UI format
 * API: { id, fareClassId, baggageType, included, numberOfPieces, weightPerPiece, size, disclaimerText, fareClassName, airlineName, cabinClassName, ... }
 * UI:  { id, fareClassId, baggageType, included, numberOfPieces, weightPerPiece, size, disclaimerText, fareClassName, airlineName, cabinClassName }
 */
export const mapApiToUi = (api) => {
  if (!api) return null;
  return {
    id: api.id,
    fareClassId: api.fareClassId,
    baggageType: api.baggageType ?? '',
    included: Boolean(api.included),
    numberOfPieces: api.numberOfPieces ?? null,
    weightPerPiece: api.weightPerPiece != null ? Number(api.weightPerPiece) : null,
    size: api.size ?? '',
    disclaimerText: api.disclaimerText ?? '',
    fareClassCode: api.fareClassCode ?? '',
    fareClassName: api.fareClassName ?? '',
    airlineName: api.airlineName ?? '',
    cabinClassName: api.cabinClassName ?? '',
  };
};

/**
 * Map UI fare option to API format for create/update
 */
export const mapUiToApi = (ui) => ({
  fareClassId: parseInt(ui.fareClassId, 10) || ui.fareClassId,
  baggageType: ui.baggageType,
  included: Boolean(ui.included),
  numberOfPieces: ui.numberOfPieces != null ? parseInt(ui.numberOfPieces, 10) : null,
  weightPerPiece: ui.weightPerPiece != null ? parseFloat(ui.weightPerPiece) : null,
  size: ui.size ? String(ui.size).trim() : null,
  disclaimerText: ui.disclaimerText ? String(ui.disclaimerText).trim() : null,
});

/**
 * useGetFareOptions - fetch fare options with optional fareClassId filter
 * @param {Object} params - { fareClassId }
 * @returns { data, loading, error, refetch }
 */
export const useGetFareOptions = (params = {}) => {
  const { fareClassId = '' } = params;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFareOptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = {};
      if (fareClassId) queryParams.fareClassId = fareClassId;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData((res.data || []).map(mapApiToUi));
      } else {
        setError(res.message || 'Failed to load fare options');
        setData([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load fare options');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [fareClassId]);

  useEffect(() => {
    fetchFareOptions();
  }, [fetchFareOptions]);

  return { data, loading, error, refetch: fetchFareOptions };
};

export const useCreateFareOption = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (payload) => {
    setLoading(true);
    try {
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.post(BASE, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to create fare option' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to create fare option' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useUpdateFareOption = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    try {
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.put(`${BASE}/${id}`, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to update fare option' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to update fare option' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useDeleteFareOption = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to delete fare option' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export { BAGGAGE_TYPES };
